import { forwardRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Box, Typography } from '@mui/material';

export interface LabelLensEmptyStateProps {
  /** Whether the current user may manage the label catalog (`manage_appointment_labels`). */
  canManage: boolean;
  /** Route to the label catalog manager. */
  labelsRoute: string;
  /** Fired when the "Set up labels" link is followed (analytics + close the menu). */
  onSetUpLabels?: () => void;
}

/**
 * Educational empty state for the calendar "Group by label" menu when the
 * practice has no labels yet. Keeps the group-by button discoverable before any
 * labels exist: managers get a link into the catalog to create them; everyone
 * else is pointed at their practice admin.
 *
 * `forwardRef` so it's safe as a direct MUI `Menu` child — the list clones its
 * focus target to attach a ref/tabIndex.
 */
export const LabelLensEmptyState = forwardRef<HTMLDivElement, LabelLensEmptyStateProps>(
  ({ canManage, labelsRoute, onSetUpLabels }, ref) => (
    <Box ref={ref} sx={{ px: 2, py: 1.5, maxWidth: 288 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <LabelOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Group by label</Typography>
      </Box>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.45 }}>
        Labels organize the day view into columns — like rooms, departments, or
        visit types.{' '}
        {canManage
          ? 'Create a few labels to start grouping your calendar.'
          : 'Your practice hasn’t set any up yet — reach out to your practice admin to get them configured.'}
      </Typography>
      {canManage && (
        <Box
          component={RouterLink}
          to={labelsRoute}
          onClick={onSetUpLabels}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            mt: 1.5,
            fontSize: 13,
            fontWeight: 600,
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <SettingsOutlinedIcon sx={{ fontSize: 16 }} />
          Set up labels
        </Box>
      )}
    </Box>
  ),
);

LabelLensEmptyState.displayName = 'LabelLensEmptyState';
